import type { ConnaroEvent, PlatformName } from '../api/types.js';

/**
 * Connaro Chat feed logic (ARCHITECTURE.md §22, §45, §66).
 *
 * Pure functions over an event list, kept apart from React so the rules that
 * actually matter — ordering, deduplication, the buffer ceiling, what a search
 * matches — can be tested without rendering anything.
 *
 * Nothing here is a security boundary. Tenant scoping happened server-side
 * before these events reached the socket (§21, §47); the browser never filters
 * for isolation, only for display.
 */

/**
 * Most events held in memory.
 *
 * An operator watching a busy channel for eight hours would otherwise grow this
 * list without limit until the tab dies. Older events are dropped, not hidden:
 * history belongs to retention (§31) and search over it to Phase 12, not to a
 * browser buffer.
 */
export const MAX_BUFFERED_EVENTS = 1_000;

export type PlatformFilter = 'ALL' | PlatformName;

/**
 * Append an event, keeping the buffer ordered, deduplicated and bounded.
 *
 * Returns the SAME array reference when nothing changed, so React can skip a
 * re-render on a duplicate rather than re-rendering an identical list.
 */
export function appendEvent(events: ConnaroEvent[], incoming: ConnaroEvent): ConnaroEvent[] {
  if (events.some((existing) => existing.id === incoming.id)) {
    /*
     * The bus already drops provider redeliveries (§44), but a reconnecting
     * socket can legitimately replay what it just delivered. Showing the
     * operator the same message twice would be a visible defect.
     */
    return events;
  }

  const next = [...events, incoming];
  next.sort(compareEvents);

  return next.length > MAX_BUFFERED_EVENTS ? next.slice(next.length - MAX_BUFFERED_EVENTS) : next;
}

/**
 * Order events for display (§45).
 *
 * `occurredAt` is primary because that is when the viewer actually said it.
 * Providers introduce delay and clock skew, so ties are broken by `receivedAt`
 * and finally by `id` — a total order, so the list never reshuffles between
 * renders for events that compare equal.
 */
export function compareEvents(a: ConnaroEvent, b: ConnaroEvent): number {
  const byOccurred = Date.parse(a.occurredAt) - Date.parse(b.occurredAt);
  if (byOccurred !== 0 && !Number.isNaN(byOccurred)) return byOccurred;

  const byReceived = Date.parse(a.receivedAt) - Date.parse(b.receivedAt);
  if (byReceived !== 0 && !Number.isNaN(byReceived)) return byReceived;

  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Text a search should look at: the message and who sent it. */
function searchableText(event: ConnaroEvent): string {
  return [
    event.message?.text ?? '',
    event.actor?.displayName ?? '',
    event.actor?.username ?? '',
  ].join(' ');
}

export type FeedFilters = {
  platform: PlatformFilter;
  /** Free text. Empty means no search. */
  query: string;
};

/**
 * Apply the operator's filters.
 *
 * Case-insensitive substring matching, not a query language: an operator
 * scanning a live feed is looking for a word, and anything cleverer would
 * surprise them. Server-side search across retained history (§66) is a
 * different feature with different semantics.
 */
export function filterEvents(events: ConnaroEvent[], filters: FeedFilters): ConnaroEvent[] {
  const query = filters.query.trim().toLocaleLowerCase();

  return events.filter((event) => {
    if (filters.platform !== 'ALL' && event.platform !== filters.platform) {
      return false;
    }
    if (query === '') {
      return true;
    }
    return searchableText(event).toLocaleLowerCase().includes(query);
  });
}

/**
 * Split message text around mentions of `username` (§22: mention highlighting).
 *
 * Returns alternating plain and mention segments so the renderer can emphasise
 * matches WITHOUT building HTML. That is the whole point: chat is untrusted
 * user content (§56, §57), and the only safe way to highlight inside it is to
 * hand React an array of text nodes and let it escape every one.
 *
 * Matching is deliberately narrow — `@name`, case-insensitively, not bounded by
 * a trailing word character — so `@ann` does not light up on `@annabel`.
 */
export type MessageSegment = { text: string; mention: boolean };

export function segmentMentions(text: string, username: string | undefined): MessageSegment[] {
  const handle = username?.trim();

  if (handle === undefined || handle === '') {
    return [{ text, mention: false }];
  }

  const segments: MessageSegment[] = [];
  const needle = `@${handle}`.toLocaleLowerCase();
  const haystack = text.toLocaleLowerCase();

  let cursor = 0;

  for (;;) {
    const found = haystack.indexOf(needle, cursor);
    if (found === -1) break;

    const after = text.charAt(found + needle.length);
    // A mention ends at a non-word character; `@ann` inside `@annabel` is not
    // a mention of `ann`.
    if (/[\p{L}\p{N}_]/u.test(after)) {
      cursor = found + needle.length;
      continue;
    }

    if (found > cursor) {
      segments.push({ text: text.slice(cursor, found), mention: false });
    }
    segments.push({ text: text.slice(found, found + needle.length), mention: true });
    cursor = found + needle.length;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), mention: false });
  }

  return segments.length === 0 ? [{ text, mention: false }] : segments;
}

/** Does this event mention the operator? Drives row emphasis. */
export function mentionsUser(event: ConnaroEvent, username: string | undefined): boolean {
  const text = event.message?.text;

  /*
   * `typeof`, not `=== undefined`. A follow, subscription or gift has no
   * message text, and the API used to send that absence as `null` rather than
   * omitting the key — which slipped past an `=== undefined` guard and crashed
   * the whole feed on `null.toLocaleLowerCase()`. The API no longer emits null
   * (see `present` in apps/api/src/routes/chat.ts); this refuses to depend on
   * that, because a blank chat page is too expensive an answer to a value the
   * type said could not arrive.
   */
  if (typeof text !== 'string' || username === undefined) return false;

  return segmentMentions(text, username).some((segment) => segment.mention);
}

/** Per-platform counts for the filter row. */
export function countByPlatform(events: ConnaroEvent[]): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const event of events) {
    counts[event.platform] = (counts[event.platform] ?? 0) + 1;
  }
  return counts;
}
